import { Vector3 } from "three";

import { GroupState } from "../../lib/ik/group/group";
import { SkeletalFrame } from "../../lib/types/statsApi";

import { Context } from "../context";
import { UpdateReturnData } from "./channels/msg";

export class FrameBufferManager {
  private contextRef?: Context;

  private streamStartTime = +Infinity;

  // allow for some missing frames in buffer construction
  private marginSeconds = 10 / (30 - 1);
  private bufferTolerance = 1 / (15 - 1);

  // set this to enable intermediate buffering
  private updateSize?: number;
  private intermediateBuffer: SkeletalFrame[] = [];

  private prevChunkIndex?: number;

  lastFrameNum = -1;

  constructor(context: Context, updateSize?: number) {
    this.contextRef = context;
    this.updateSize = updateSize;
  }

  update(data: UpdateReturnData) {
    if (!this.contextRef) return;

    const debugging = this.contextRef?.state.debugSeeking;

    const { chunk, chunkIndex } = data;
    const { clock, frames, ui } = this.contextRef;

    // preprocess and find extent of chunk
    let chunkStartTime: number = +Infinity;
    let chunkEndTime: number = -Infinity;

    for (const frame of chunk.skeletalData.frames) {
      // TODO: remove debug stuff
      if (frame.time < this.streamStartTime) this.streamStartTime = frame.time;
      if (frame.time < chunkStartTime) chunkStartTime = frame.time;
      if (frame.time > chunkEndTime) chunkEndTime = frame.time;
    }

    chunkStartTime /= 1000;
    chunkEndTime /= 1000;
    // check to see if new buffer is contiguous with ring buffer
    if (chunkStartTime > clock.bufferLimits[1]) {
      if (
        chunkIndex == this.prevChunkIndex ||
        chunkStartTime <= clock.bufferLimits[1] + this.marginSeconds
      ) {
        if (debugging) console.log(`chunk ${chunkIndex} contiguous, appending`);
        // add frames to ring buffer
        this.intermediateBuffer.push(...chunk.skeletalData.frames);
      } else {
        // jumped forward - discontinuity
        if (debugging)
          console.warn(
            `chunk ${chunkIndex} jumped forward - discontinuity (${chunkStartTime} >> ${clock.bufferLimits[1]})`
          );
        frames.clear();
        this.intermediateBuffer.push(...chunk.skeletalData.frames);
      }
    } else {
      if (chunkStartTime > clock.bufferLimits[0]) {
        console.error(`fetched chunk ${chunkIndex} already in buffer`);
      } else {
        if (debugging)
          console.warn(
            `chunk ${chunkIndex} jumped backward - discontinuity (${chunkStartTime} < ${clock.bufferLimits[0]})`
          );
        // jumped backward - discontinuity
        frames.clear();
        this.intermediateBuffer.push(...chunk.skeletalData.frames);
      }
    }

    this.prevChunkIndex = chunkIndex;

    // update ui
    ui?.setTimelinePeriod(clock.timelinePeriod);

    // TODO: tick here to keep things deterministic - if we want to use
    //       intermediateBuffer however, we need to tick elsewhere
    this.tick();
  }

  tick() {
    if (this.contextRef && this.intermediateBuffer.length > 0) {
      const { clock, frames, ui } = this.contextRef;

      const toAdd = this.intermediateBuffer.splice(
        0,
        this.updateSize ?? this.intermediateBuffer.length
      );

      for (const frame of toAdd) {
        for (const pos of frame.positions) {
          if (pos.positionId == 0) continue;

          if (pos.posed) {
            const state: GroupState = GroupState.clone(pos.posed.state);
            pos.posed.state = state;
            pos.posed.centroid = Object.assign(
              new Vector3(),
              pos.posed.centroid
            );
          }
        }
      }

      // add all or some of the buffered frames to the main ring buffer
      frames.add(...toAdd);

      const last = frames.getLast();
      if (last) this.lastFrameNum = last.num;

      // update clock to reflect frames in buffer
      // TODO: test for contiguous, ascending frames elsewhere and just take 1st and
      //       last frames here
      let bufferStart = +Infinity;
      let bufferEnd = -Infinity;
      const bufferLength = frames.getBufferLength();
      for (let i = 0; i < bufferLength; i++) {
        const frame = frames.get(i);
        if (frame) {
          if (frame.time < bufferStart) bufferStart = frame.time;
          if (frame.time > bufferEnd) bufferEnd = frame.time;
        }
      }
      clock.bufferLimits = [bufferStart / 1000, bufferEnd / 1000];
      clock.timelineLimits = [
        Math.min(clock.bufferLimits[0], clock.timelineLimits[0]),
        Math.max(clock.bufferLimits[1], clock.timelineLimits[1]),
      ];

      // handle fact that startTimestamp of record might not be exactly timestamp
      // of first frame
      if (
        clock.real < clock.bufferLimits[0] &&
        clock.bufferLimits[0] - clock.real < this.bufferTolerance
      ) {
        clock.real = clock.bufferLimits[0];
      }
    }
  }
}
